import React from "react";
import { useTranslation } from "react-i18next";
import { corridoLook, onCorridoLook, setCorridoLook, type CorridoLook } from "@/lib/corrido-api";
import { fetchCorrido3dAccess, type Corrido3dAccess } from "@/lib/corrido-access";

// The corrido flow's look: Realism (the product everyone has) or FaceShot
// Animation, the 3D animated twin (services/corrido-3d). The server decides
// who may START 3D work (GET /api/web/corrido3d/access, lib/corrido-access.ts);
// this file only shows what it says:
//   * CorridoLookChoice, in the flow's first step: Realism or FaceShot
//     Animation, for an account the 3D product is open to (owner, 2026-10-11:
//     "provide the feature in the corrido music video for the users to choose
//     between making a realism music video or a faceshot animation music
//     video"). An account it is not open to sees the first step as before;
//   * CorridoLookBar, above the flow: on the 3D flow, a FaceShot Animation
//     badge and a way back to Realism; a ?look=3d an account cannot use falls
//     back to Realism with one plain sentence. On the realistic flow it shows
//     nothing.
// Choosing a look re-opens the flow from scratch on that product (its runs,
// its board and its polls belong to the product they started on).

/** The current look, re-rendering on a switch. */
export function useCorridoLook(): CorridoLook {
  const [look, setLook] = React.useState<CorridoLook>(corridoLook());
  React.useEffect(() => onCorridoLook(setLook), []);
  return look;
}

/** The account's 3D access: null while unknown (a failed ask too), or signed out. */
export function useCorrido3dAccess(): { access: Corrido3dAccess | null; signedOut: boolean } {
  const [state, setState] = React.useState<{ access: Corrido3dAccess | null; signedOut: boolean }>({ access: null, signedOut: false });
  React.useEffect(() => {
    const pending = fetchCorrido3dAccess();
    if (!pending) { setState({ access: null, signedOut: true }); return undefined; }
    let alive = true;
    void pending.then((a) => { if (alive) setState({ access: a, signedOut: false }); });
    return () => { alive = false; };
  }, []);
  return state;
}

const S = {
  bar: {
    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" as const,
    padding: "8px 12px", margin: "0 0 10px", borderRadius: 10,
    border: "1px solid #2a3648", background: "#0a0e15", color: "#e2e8f0", fontSize: 13,
  },
  badge: {
    padding: "2px 8px", borderRadius: 999, background: "#e3b341", color: "#05070b",
    fontWeight: 800, fontSize: 12, letterSpacing: 0.3,
  },
  note: { color: "#94a3b8", fontSize: 12 },
  button: {
    marginLeft: "auto", padding: "6px 10px", borderRadius: 8, border: "1px solid #405068",
    background: "#101722", color: "#ffffff", fontSize: 12, fontWeight: 700, cursor: "pointer",
  },
  choice: { display: "grid", gap: 6, margin: "0 0 8px" },
  choiceTitle: { color: "#e2e8f0", fontSize: 12, fontWeight: 700, letterSpacing: 0.3 },
  options: { display: "flex", gap: 8, flexWrap: "wrap" as const },
  option: (on: boolean) => ({
    flex: "1 1 160px", borderRadius: 10, padding: "10px 12px", textAlign: "left" as const, cursor: "pointer",
    border: `1px solid ${on ? "#e3b341" : "#2a3648"}`, background: on ? "rgba(227,179,65,.10)" : "#0a0e15",
    color: "#e2e8f0",
  }),
  optionName: { color: "#ffffff", fontSize: 14, fontWeight: 700 },
  optionDesc: { color: "#94a3b8", fontSize: 12 },
};

/**
 * Realism or FaceShot Animation, in the flow's first step. Renders nothing
 * unless the server says this account may make the 3D version.
 */
export function CorridoLookChoice() {
  const { t } = useTranslation();
  const look = useCorridoLook();
  const { access } = useCorrido3dAccess();
  if (!access || !access.allowed) return null;
  const options: Array<{ id: CorridoLook; name: string; desc: string }> = [
    { id: "real", name: t("corrido.look.real"), desc: t("corrido.look.realDesc") },
    { id: "3d", name: t("corrido.look.animated"), desc: t("corrido.look.animatedDesc") },
  ];
  return (
    <div style={S.choice} role="radiogroup" aria-label={t("corrido.look.title")} data-testid="corrido-look-choice">
      <div style={S.choiceTitle}>{t("corrido.look.title")}</div>
      <div style={S.options}>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={look === o.id}
            data-testid={`corrido-look-${o.id}`}
            onClick={() => setCorridoLook(o.id)}
            style={S.option(look === o.id)}
          >
            <span style={S.optionName}>{o.name}</span><br />
            <span style={S.optionDesc}>{o.desc}</span>
          </button>
        ))}
      </div>
      {look === "3d" && access.access === "admin" && <div style={S.note}>{t("corrido.look3d.ownerNote")}</div>}
    </div>
  );
}

export default function CorridoLookBar() {
  const { t } = useTranslation();
  const look = useCorridoLook();
  const { access, signedOut } = useCorrido3dAccess();
  const [fellBack, setFellBack] = React.useState(false);

  React.useEffect(() => {
    if (look !== "3d") return;
    if (signedOut || (access && !access.allowed)) {
      setCorridoLook("real");
      setFellBack(true);
    }
  }, [look, access, signedOut]);

  if (fellBack && look === "real") {
    return <div style={S.bar} data-testid="corrido-look-fallback">{t("corrido.look3d.unavailable")}</div>;
  }
  if (look !== "3d" || !access || !access.allowed) return null;
  return (
    <div style={S.bar} data-testid="corrido-look-3d">
      <span style={S.badge}>{t("corrido.look3d.badge")}</span>
      {access.access === "admin" && <span style={S.note}>{t("corrido.look3d.ownerNote")}</span>}
      <button type="button" style={S.button} onClick={() => setCorridoLook("real")}>
        {t("corrido.look3d.switchReal")}
      </button>
    </div>
  );
}
